import React from 'react';
// 移除钉钉认证依赖

export type PermissionType = 'microphone' | 'camera';

interface PermissionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onRetry: () => void;
    permissionType: PermissionType;
    errorMessage: string;
}

const PermissionModal: React.FC<PermissionModalProps> = ({
    isOpen,
    onClose,
    onRetry,
    permissionType,
    errorMessage
}) => {
    if (!isOpen) return null;

    const getPermissionConfig = () => {
        switch (permissionType) {
            case 'microphone':
                return {
                    icon: (
                        <svg className="w-8 h-8 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                            <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
                        </svg>
                    ),
                    title: '需要麦克风权限',
                    bgColor: 'bg-red-100',
                    buttonColor: 'bg-blue-500 hover:bg-blue-600',
                    dingTalkSolutions: [
                        '确保在钉钉客户端中打开应用',
                        '检查钉钉客户端的麦克风权限设置',
                        '联系管理员确认应用权限配置',
                        '尝试重启钉钉客户端后重新进入',
                        '确保设备麦克风正常工作'
                    ],
                    browserSolutions: [
                        '点击地址栏左侧的🔒图标',
                        '选择"允许"麦克风权限',
                        '刷新页面后重新尝试',
                        '确保麦克风设备已连接',
                        '检查浏览器是否支持麦克风'
                    ]
                };
            case 'camera':
                return {
                    icon: (
                        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                    ),
                    title: '需要相机权限',
                    bgColor: 'bg-green-100',
                    buttonColor: 'bg-green-500 hover:bg-green-600',
                    dingTalkSolutions: [
                        '确保在钉钉客户端中打开应用',
                        '检查钉钉客户端的相机权限设置',
                        '联系管理员确认应用权限配置',
                        '尝试重启钉钉客户端后重新进入',
                        '确保设备相机正常工作'
                    ],
                    browserSolutions: [
                        '点击地址栏左侧的🔒图标',
                        '选择"允许"相机权限',
                        '刷新页面后重新尝试',
                        '确保相机设备已连接',
                        '检查浏览器是否支持相机'
                    ]
                };
            default:
                return {
                    icon: null,
                    title: '需要权限',
                    bgColor: 'bg-gray-100',
                    buttonColor: 'bg-blue-500 hover:bg-blue-600',
                    dingTalkSolutions: [],
                    browserSolutions: []
                };
        }
    };

    const config = getPermissionConfig();
    const isDingTalk = false; // 移除钉钉环境检测

    return (
        <>
            {/* 遮罩层 */}
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                {/* 模态框内容 */}
                <div className="bg-white rounded-2xl p-6 w-full max-w-sm mx-auto">
                    <div className="text-center">
                        {/* 权限图标 */}
                        <div className={`w-16 h-16 mx-auto mb-4 ${config.bgColor} rounded-full flex items-center justify-center`}>
                            {config.icon}
                        </div>

                        {/* 标题 */}
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            {config.title}
                        </h3>

                        {/* 错误信息 */}
                        <p className="text-gray-600 mb-6 leading-relaxed">
                            {errorMessage}
                        </p>

                        {/* 解决方案提示 */}
                        <div className="bg-blue-50 rounded-lg p-4 mb-6 text-left">
                            <h4 className="text-sm font-medium text-blue-900 mb-2">解决方法：</h4>
                            <ul className="text-sm text-blue-800 space-y-1">
                                {(isDingTalk ? config.dingTalkSolutions : config.browserSolutions).map((solution, index) => (
                                    <li key={index}>• {solution}</li>
                                ))}
                            </ul>
                        </div>

                        {/* 操作按钮 */}
                        <div className="flex space-x-3">
                            <button
                                onClick={onClose}
                                className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                            >
                                知道了
                            </button>
                            <button
                                onClick={() => {
                                    onClose();
                                    // 延迟一点再重试，让模态框先关闭
                                    setTimeout(() => {
                                        onRetry();
                                    }, 500);
                                }}
                                className={`flex-1 py-3 px-4 text-white rounded-lg font-medium transition-colors ${config.buttonColor}`}
                            >
                                重新尝试
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PermissionModal;
